import { useState, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, X, Loader2, FileText } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

export default function PdfViewer({ url, title, description, onClose, onDownload }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setLoading(false);
  }, []);

  const onDocumentLoadError = useCallback(() => {
    setLoadError(true);
    setLoading(false);
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-white dark:bg-slate-900 flex flex-col" data-testid="pdf-viewer-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50 dark:bg-slate-800 flex-shrink-0">
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-base text-slate-900 dark:text-white truncate">{title}</h2>
          {description && <p className="text-xs text-slate-500 truncate">{description}</p>}
        </div>
        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
          {/* Page controls */}
          {numPages && numPages > 1 && (
            <div className="flex items-center gap-1 mr-2 border-r pr-3">
              <Button variant="ghost" size="sm" disabled={pageNumber <= 1} onClick={() => setPageNumber(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 min-w-[60px] text-center">{pageNumber} / {numPages}</span>
              <Button variant="ghost" size="sm" disabled={pageNumber >= numPages} onClick={() => setPageNumber(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          {/* Zoom controls */}
          <div className="flex items-center gap-1 mr-2 border-r pr-3">
            <Button variant="ghost" size="sm" onClick={() => setScale(s => Math.max(0.5, s - 0.2))} title="Reducir">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-xs text-slate-500 min-w-[40px] text-center">{Math.round(scale * 100)}%</span>
            <Button variant="ghost" size="sm" onClick={() => setScale(s => Math.min(3, s + 0.2))} title="Ampliar">
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
          {onDownload && (
            <Button variant="outline" size="sm" onClick={onDownload} data-testid="pdf-download-btn">
              <Download className="w-4 h-4 mr-1" /> Descargar
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose} data-testid="close-pdf-viewer">
            <X className="w-4 h-4 mr-1" /> Cerrar
          </Button>
        </div>
      </div>

      {/* PDF Content */}
      <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-950 flex justify-center">
        {loadError ? (
          <div className="flex flex-col items-center justify-center gap-4 text-slate-500 py-20">
            <FileText className="w-16 h-16 text-slate-300" />
            <p className="text-lg font-medium text-slate-700 dark:text-slate-300">No se pudo cargar el documento</p>
            <p className="text-sm">El archivo PDF no se encuentra disponible.</p>
            {onDownload && (
              <Button variant="outline" onClick={onDownload}>
                <Download className="w-4 h-4 mr-2" /> Descargar archivo
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>Volver</Button>
          </div>
        ) : (
          <div className="py-4">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                <span className="ml-3 text-slate-500">Cargando documento...</span>
              </div>
            )}
            <Document
              file={url}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                className="shadow-lg mx-auto"
              />
            </Document>
          </div>
        )}
      </div>
    </div>
  );
}
